import { useReducer } from 'react'
import { useDispatch } from 'react-redux'
import { assoc, isNil, reduce } from 'ramda'

import effects from 'app/effects/effects'
import { usePlace } from 'commons/usePlace'
import { mapBoolToNum } from 'commons/boolean'
import { exportCsv } from 'app/utilize/generate-csv'
import { networkErrorToMessage } from 'commons/error'

import { filterStatus, headerCVS } from 'app/constants/subscriberCRM'
import {
  TYPE_ALL,
  TYPE_STATUS,
  TYPE_SELECTED,
  STATUS_PENDING,
} from 'app/constants/subscriberCRM'

import MessageAlert from 'app/components/MessageAlert'

export const ActionType = {
  SET_STATUS: 'SET_STATUS',
  SET_IS_LOADING_EXPORT: 'SET_IS_LOADING_EXPORT',
  SET_COUNT_PENDING: 'SET_COUNT_PENDING',
  SET_IS_DIALOG_APPROVE_PENDING: 'SET_IS_DIALOG_APPROVE_PENDING',
  SET_IS_LOADING_APPROVE: 'SET_IS_LOADING_APPROVE',
}

const initialState = {
  status: filterStatus,
  isLoadingExport: false,
  countPending: 0,
  isDialogApprovePending: false,
  isLoadingApprove: false,
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_STATUS:
      return {
        ...state,
        status: action.value,
      }
    case ActionType.SET_IS_LOADING_EXPORT:
      return {
        ...state,
        isLoadingExport: action.value,
      }
    case ActionType.SET_COUNT_PENDING:
      return {
        ...state,
        countPending: action.value,
        isDialogApprovePending: action.value > 0,
      }
    case ActionType.SET_IS_DIALOG_APPROVE_PENDING:
      return {
        ...state,
        isDialogApprovePending: action.value,
      }
    case ActionType.SET_IS_LOADING_APPROVE:
      return {
        ...state,
        isLoadingApprove: action.value,
      }
    default:
      return state
  }
}

export const useAction = (formatMessage) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const place = usePlace()
  const commonDispatch = useDispatch()

  const switchAutoApprove = async (bool, acceptAllPending = false) => {
    dispatch({
      type: 'SET_IS_LOADING_APPROVE',
      value: true,
    })
    await effects.apis
      .autoApproval({
        placeId: place.id,
        data: {
          autoApprove: mapBoolToNum(bool),
          autoApproveAccept: mapBoolToNum(acceptAllPending),
        },
      })
      .then(() => {
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({ id: 'text.change.success' }),
          }),
        )
        dispatch({
          type: 'SET_IS_LOADING_APPROVE',
          value: false,
        })
      })
      .catch((error) => {
        const { message } = networkErrorToMessage(formatMessage)(error)

        commonDispatch(
          MessageAlert.showError({
            message: message ?? formatMessage({ id: 'error.general' }),
          }),
        )
      })
  }

  const mapChannelsWithCount = (counter) => {
    const counterObject = reduce(
      (obj, item) => assoc(item.invitation_status, item, obj),
      {},
      counter,
    )

    return filterStatus.map((status) => {
      return isNil(counterObject[status.name])
        ? status
        : { ...status, count: counterObject[status.name].count ?? 0 }
    })
  }

  const countSubscribersStatus = async () => {
    await effects.apis
      .subscribersCountForStatuses({
        placeId: place.id,
      })
      .then(async (response) => {
        dispatch({
          type: 'SET_STATUS',
          value: await mapChannelsWithCount(response.data),
        })
      })
      .catch((error) => {
        const { message } = networkErrorToMessage(formatMessage)(error)

        dispatch(
          MessageAlert.showError({
            message: message ?? formatMessage({ id: 'error.general' }),
          }),
        )
      })
  }

  const handleExportCSV = (data) => {
    exportCsv({
      headerCVS: headerCVS(formatMessage),
      dataCSV: data,
      fileName: formatMessage({
        id: 'group.subscribers',
      }),
    })
  }

  const handleExportType = (key, selectedIds, status) => {
    const defaultPayload = {
      page: 1,
      limit: 100000,
    }
    if (key === TYPE_ALL) return defaultPayload
    else if (key === TYPE_SELECTED)
      return { ...defaultPayload, ids: selectedIds }
    else if (key === TYPE_STATUS) return { ...defaultPayload, status: status }
  }

  const exportSubscribers = async (
    key = TYPE_ALL,
    selectedIds = null,
    status = null,
  ) => {
    dispatch({
      type: 'SET_IS_LOADING_EXPORT',
      value: true,
    })
    await effects.apis
      .exportSubscribers(handleExportType(key, selectedIds, status))
      .then(({ data }) => {
        handleExportCSV(data)
        dispatch({
          type: 'SET_IS_LOADING_EXPORT',
          value: false,
        })
      })
      .catch((error) => {
        const { message } = networkErrorToMessage(formatMessage)(error)

        dispatch(
          MessageAlert.showError({
            message: message ?? formatMessage({ id: 'error.general' }),
          }),
        )
      })
  }

  const fetchSubscribersPending = async () => {
    let count = null
    await effects.apis
      .subscribersV2({
        page: 1,
        limit: 0,
        status: STATUS_PENDING,
      })
      .then(({ meta: { total } }) => {
        dispatch({
          type: 'SET_COUNT_PENDING',
          value: total,
        })
        count = total
      })
      .catch((error) => {
        return error
      })
    return count
  }

  const handleSubscribersPendingDialog = (bool) => {
    dispatch({
      type: 'SET_IS_DIALOG_APPROVE_PENDING',
      value: bool,
    })
  }

  return {
    state,
    exportSubscribers,
    switchAutoApprove,
    countSubscribersStatus,
    fetchSubscribersPending,
    handleSubscribersPendingDialog,
  }
}
